<template>
    <v-dialog :model-value="dialogRelatorio" @update:model-value="updateDialog" max-width="1200px" persistent>
      <v-card>
        <v-card-title class="d-flex align-center bg-primary text-white">
          <v-icon class="mr-2">mdi-file-chart</v-icon>
          Relatório - Ordem #{{ dadosRelatorio.ordem }}
          <v-spacer />
          <v-btn icon variant="text" @click="fecharRelatorio">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
  
        <v-card-text class="pa-0">
          <!-- Botões de Ação -->
          <v-toolbar flat color="grey-lighten-4">
            <v-btn color="error" @click="exportarPDF" :loading="exportando" prepend-icon="mdi-file-pdf">
              Baixar PDF
            </v-btn>
            <v-btn color="info" @click="imprimir" class="ml-2" prepend-icon="mdi-printer">
              Imprimir
            </v-btn>
            <v-spacer />
            <v-btn color="warning" @click="debugDados" variant="outlined" size="small">
              🐛 Debug
            </v-btn>
          </v-toolbar>
  
          <!-- DEBUG INFO -->
          <v-alert v-if="mostrarDebug" type="info" class="ma-4">
            <div><strong>Total de setores:</strong> {{ (dadosRelatorio.setores || []).length }}</div>
            <div><strong>Dados disponíveis:</strong> {{ Object.keys(dadosRelatorio).join(', ') }}</div>
            <div v-if="dadosRelatorio.setores && dadosRelatorio.setores.length > 0">
              <strong>Primeiro setor:</strong>
              <pre style="font-size: 10px;">{{ JSON.stringify(dadosRelatorio.setores[0], null, 2) }}</pre>
            </div>
          </v-alert>
  
          <!-- Conteúdo do Relatório -->
          <div class="relatorio-content pa-4" ref="relatorioContent">
            <!-- Cabeçalho -->
            <div class="text-center mb-6">
              <h1 class="text-h4 primary--text mb-2">🫀 Relatório do Coração da Produção</h1>
              <h2 class="text-h5 mb-2">Ordem #{{ dadosRelatorio.ordem }}</h2>
              <p class="text-body-1">{{ dadosRelatorio.data }}</p>
            </div>
  
            <!-- Métricas Principais -->
            <v-row class="mb-6">
              <v-col cols="12">
                <h3 class="text-h6 mb-3">📊 Resumo Executivo</h3>
              </v-col>
              <v-col cols="6" md="3">
                <v-card class="text-center pa-3" color="blue-lighten-5">
                  <div class="text-h5 font-weight-bold primary--text">{{ dadosRelatorio.metricas.planejado }}</div>
                  <div class="text-caption">Meta Total</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card class="text-center pa-3" color="green-lighten-5">
                  <div class="text-h5 font-weight-bold success--text">{{ dadosRelatorio.metricas.produzido }}</div>
                  <div class="text-caption">Produzido</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card class="text-center pa-3" color="orange-lighten-5">
                  <div class="text-h5 font-weight-bold warning--text">{{ dadosRelatorio.metricas.restante }}</div>
                  <div class="text-caption">Restante</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card class="text-center pa-3" :color="dadosRelatorio.metricas.erros > 0 ? 'red-lighten-5' : 'green-lighten-5'">
                  <div class="text-h5 font-weight-bold" :class="dadosRelatorio.metricas.erros > 0 ? 'error--text' : 'success--text'">
                    {{ dadosRelatorio.metricas.erros }}
                  </div>
                  <div class="text-caption">Erros</div>
                </v-card>
              </v-col>
            </v-row>
  
            <!-- Progresso Geral -->
            <v-row class="mb-6">
              <v-col cols="12">
                <h4 class="text-subtitle-1 mb-2">Progresso Geral: {{ dadosRelatorio.metricas.eficiencia }}%</h4>
                <v-progress-linear
                  :model-value="parseFloat(dadosRelatorio.metricas.eficiencia)"
                  height="20"
                  rounded
                  :color="getCorProgresso(parseFloat(dadosRelatorio.metricas.eficiencia))"
                >
                  <template v-slot:default="{ value }">
                    <strong>{{ Math.ceil(value) }}%</strong>
                  </template>
                </v-progress-linear>
              </v-col>
            </v-row>
  
            <!-- SEÇÃO: Detalhes por Setor -->
            <div class="mb-6">
              <h3 class="text-h6 mb-3">🏭 Status Detalhado por Setor</h3>
              
              <!-- Verificar se temos setores -->
              <div v-if="!dadosRelatorio.setores || dadosRelatorio.setores.length === 0">
                <v-alert type="warning" density="compact">
                  <strong>⚠️ Nenhum dado de setor encontrado!</strong><br>
                  Verifique se os dados estão sendo passados corretamente.
                </v-alert>
              </div>
              
              <!-- Mostrar setores se existirem -->
              <div v-else>
                <v-alert type="success" density="compact" class="mb-3">
                  <strong>✅ {{ dadosRelatorio.setores.length }} setor(es) encontrado(s)</strong>
                </v-alert>
                
                <v-table density="compact" class="mb-4">
                  <thead>
                    <tr>
                      <th>Ordem</th>
                      <th>Setor</th>
                      <th>Parte</th>
                      <th>Tamanho</th>
                      <th>Planejado</th>
                      <th>Recebido</th>
                      <th>Produzido</th>
                      <th>Disponível</th>
                      <th>Pend. Aprovação</th>
                      <th>Progresso</th>
                      <th>Status</th>
                      <th>Responsável</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(setor, index) in getSetoresOrdenados()" :key="index">
                      <td>
                        <v-chip size="x-small" color="grey" variant="flat">
                          {{ setor.ordem_exibicao_tela || (index + 1) }}º
                        </v-chip>
                      </td>
                      <td><strong>{{ setor.nome_setor || 'N/A' }}</strong></td>
                      <td>
                        <v-chip size="x-small" color="blue" variant="flat">
                          {{ setor.parte || 'N/A' }}
                        </v-chip>
                      </td>
                      <td>
                        <v-chip size="x-small" color="green" variant="flat">
                          {{ setor.tamanho || 'N/A' }}
                        </v-chip>
                      </td>
                      <td>{{ setor.planejada_inicial || 0 }}</td>
                      <td>{{ setor.recebida_aprovada || 0 }}</td>
                      <td>{{ setor.produzida || 0 }}</td>
                      <td>{{ setor.saldo_disponivel_produzir || 0 }}</td>
                      <td>
                        <span v-if="(setor.recebida_aguardando_aprovocao || 0) > 0" class="error--text font-weight-bold">
                          {{ setor.recebida_aguardando_aprovocao }}
                        </span>
                        <span v-else class="success--text">0</span>
                      </td>
                      <td>
                        <div class="d-flex align-center">
                          <v-progress-linear
                            :model-value="calcularProgresso(setor)"
                            height="8"
                            rounded
                            :color="getCorProgresso(calcularProgresso(setor))"
                            class="mr-2"
                            style="min-width: 60px;"
                          />
                          <span class="text-caption">{{ calcularProgresso(setor).toFixed(0) }}%</span>
                        </div>
                      </td>
                      <td>
                        <v-chip 
                          size="x-small" 
                          :color="getStatusCor(setor)"
                          variant="flat"
                        >
                          {{ getStatusTexto(setor) }}
                        </v-chip>
                      </td>
                      <td class="text-caption">{{ setor.funcionario_atual_nome || 'Não atribuído' }}</td>
                    </tr>
                  </tbody>
                </v-table>
  
                <!-- Resumo por Parte/Tamanho -->
                <div class="mt-4">
                  <h4 class="text-subtitle-1 mb-2">📋 Resumo por Categoria</h4>
                  <v-row>
                    <!-- Resumo por Parte -->
                    <v-col cols="12" md="6">
                      <v-card variant="outlined" class="pa-3">
                        <h5 class="text-subtitle-2 mb-2">📦 Por Parte:</h5>
                        <div v-if="getResumoPartes().length > 0">
                          <div v-for="parte in getResumoPartes()" :key="parte.nome" class="d-flex justify-space-between align-center mb-2">
                            <div class="d-flex align-center">
                              <v-chip size="x-small" color="blue" variant="outlined" class="mr-2">{{ parte.nome }}</v-chip>
                              <span class="text-body-2">{{ parte.produzido }}/{{ parte.planejado }}</span>
                            </div>
                            <div class="d-flex align-center">
                              <v-progress-linear
                                :model-value="parte.progresso"
                                height="6"
                                rounded
                                :color="getCorProgresso(parte.progresso)"
                                class="mr-2"
                                style="min-width: 80px;"
                              />
                              <span class="text-caption font-weight-bold">{{ parte.progresso.toFixed(0) }}%</span>
                            </div>
                          </div>
                        </div>
                        <div v-else class="text-caption grey--text">Nenhuma parte identificada</div>
                      </v-card>
                    </v-col>
  
                    <!-- Resumo por Tamanho -->
                    <v-col cols="12" md="6">
                      <v-card variant="outlined" class="pa-3">
                        <h5 class="text-subtitle-2 mb-2">📏 Por Tamanho:</h5>
                        <div v-if="getResumoTamanhos().length > 0">
                          <div v-for="tamanho in getResumoTamanhos()" :key="tamanho.nome" class="d-flex justify-space-between align-center mb-2">
                            <div class="d-flex align-center">
                              <v-chip size="x-small" color="green" variant="outlined" class="mr-2">{{ tamanho.nome }}</v-chip>
                              <span class="text-body-2">{{ tamanho.produzido }}/{{ tamanho.planejado }}</span>
                            </div>
                            <div class="d-flex align-center">
                              <v-progress-linear
                                :model-value="tamanho.progresso"
                                height="6"
                                rounded
                                :color="getCorProgresso(tamanho.progresso)"
                                class="mr-2"
                                style="min-width: 80px;"
                              />
                              <span class="text-caption font-weight-bold">{{ tamanho.progresso.toFixed(0) }}%</span>
                            </div>
                          </div>
                        </div>
                        <div v-else class="text-caption grey--text">Nenhum tamanho identificado</div>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </div>
  
            <!-- Previsões -->
            <v-row class="mb-6">
              <v-col cols="12">
                <h3 class="text-h6 mb-3">🔮 Previsões</h3>
              </v-col>
              <v-col cols="6" md="3">
                <v-card class="text-center pa-3" color="blue-lighten-5">
                  <div class="text-h6 font-weight-bold">{{ dadosRelatorio.previsao.conclusao }}</div>
                  <div class="text-caption">Previsão</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card class="text-center pa-3" color="green-lighten-5">
                  <div class="text-h6 font-weight-bold">{{ dadosRelatorio.previsao.diasRestantes }}</div>
                  <div class="text-caption">Dias Restantes</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card class="text-center pa-3" color="purple-lighten-5">
                  <div class="text-h6 font-weight-bold">{{ dadosRelatorio.previsao.ritmo }}</div>
                  <div class="text-caption">Peças/dia</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card class="text-center pa-3" :color="dadosRelatorio.previsao.risco > 50 ? 'red-lighten-5' : 'green-lighten-5'">
                  <div class="text-h6 font-weight-bold" :class="dadosRelatorio.previsao.risco > 50 ? 'error--text' : 'success--text'">
                    {{ dadosRelatorio.previsao.risco }}%
                  </div>
                  <div class="text-caption">Risco de Atraso</div>
                </v-card>
              </v-col>
            </v-row>
  
            <!-- Rodapé -->
            <div class="text-center mt-6 pt-4" style="border-top: 2px solid #ddd;">
              <p class="text-caption">
                <strong>Relatório gerado automaticamente pelo Sistema Coração da Produção</strong><br>
                {{ new Date().toLocaleString('pt-BR') }} | Versão 1.0
              </p>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </template>
  
  <script>
  export default {
    name: 'RelatorioCoracao',
    
    props: {
      dialogRelatorio: {
        type: Boolean,
        default: false
      },
      dadosRelatorio: {
        type: Object,
        required: true,
        default: () => ({
          ordem: '',
          data: '',
          metricas: { planejado: 0, produzido: 0, restante: 0, erros: 0, eficiencia: '0' },
          previsao: { conclusao: '', diasRestantes: 0, ritmo: 0, risco: 0 },
          setores: [],
          gargalos: [],
          alertas: [],
          insights: [],
          acoes: [],
          explicacao: ''
        })
      }
    },
    
    emits: ['update:dialogRelatorio', 'relatorio-exportado'],
    
    data() {
      return {
        exportando: false,
        mostrarDebug: false
      };
    },
  
    watch: {
      dadosRelatorio: {
        handler(newVal) {
          console.log('=== DADOS RELATÓRIO RECEBIDOS ===');
          console.log('Dados completos:', newVal);
          console.log('Setores:', newVal?.setores);
          console.log('Quantidade de setores:', newVal?.setores?.length || 0);
        },
        deep: true,
        immediate: true
      }
    },
    
    methods: {
      updateDialog(value) {
        this.$emit('update:dialogRelatorio', value);
      },
  
      fecharRelatorio() {
        this.updateDialog(false);
      },
  
      debugDados() {
        this.mostrarDebug = !this.mostrarDebug;
        console.log('=== DEBUG RELATÓRIO COMPLETO ===');
        console.log('Props recebidas:', this.dadosRelatorio);
        console.log('Setores disponíveis:', this.dadosRelatorio?.setores);
        console.log('Quantidade:', this.dadosRelatorio?.setores?.length || 0);
        
        if (this.dadosRelatorio?.setores?.length > 0) {
          console.log('Primeiro setor detalhado:', this.dadosRelatorio.setores[0]);
          console.log('Campos disponíveis:', Object.keys(this.dadosRelatorio.setores[0]));
        }
      },
  
      // Calcular progresso de um setor
      calcularProgresso(setor) {
        const total = setor.planejada_inicial || 0;
        const produzido = setor.produzida || 0;
        return total > 0 ? (produzido / total) * 100 : 0;
      },
  
      // Obter cor do progresso
      getCorProgresso(valor) {
        if (valor >= 80) return 'success';
        if (valor >= 60) return 'info';
        if (valor >= 40) return 'warning';
        return 'error';
      },
  
      // Obter status do setor
      getStatusTexto(setor) {
        const progresso = this.calcularProgresso(setor);
        
        if (setor.setor_finalizado_com_erro > 0) return 'Com Erro';
        if (setor.status_geral === 'completa') return 'Concluído';
        if (progresso >= 100) return 'Concluído';
        if (progresso >= 75) return 'Avançado';
        if (progresso >= 25) return 'Em Progresso';
        if (setor.recebida_aprovada > 0) return 'Iniciado';
        if (setor.status_geral === 'sem') return 'Aguardando';
        return 'Pendente';
      },
  
      // Obter cor do status
      getStatusCor(setor) {
        const status = this.getStatusTexto(setor);
        const cores = {
          'Concluído': 'success',
          'Avançado': 'info',
          'Em Progresso': 'primary',
          'Iniciado': 'warning',
          'Aguardando': 'grey',
          'Pendente': 'orange',
          'Com Erro': 'error'
        };
        return cores[status] || 'grey';
      },
  
      // Obter setores ordenados pela ordem de exibição
      getSetoresOrdenados() {
        const setores = this.dadosRelatorio?.setores || [];
        return [...setores].sort((a, b) => {
          return (a.ordem_exibicao_tela || 0) - (b.ordem_exibicao_tela || 0);
        });
      },
  
      // Resumo por partes
      getResumoPartes() {
        const setores = this.dadosRelatorio?.setores || [];
        const partes = {};
        
        setores.forEach(setor => {
          const parte = setor.parte || 'Não informado';
          if (!partes[parte]) {
            partes[parte] = { planejado: 0, produzido: 0 };
          }
          partes[parte].planejado += setor.planejada_inicial || 0;
          partes[parte].produzido += setor.produzida || 0;
        });
  
        return Object.keys(partes).map(nome => ({
          nome,
          planejado: partes[nome].planejado,
          produzido: partes[nome].produzido,
          progresso: partes[nome].planejado > 0 ? (partes[nome].produzido / partes[nome].planejado) * 100 : 0
        }));
      },
  
      // Resumo por tamanhos
      getResumoTamanhos() {
        const setores = this.dadosRelatorio?.setores || [];
        const tamanhos = {};
        
        setores.forEach(setor => {
          const tamanho = setor.tamanho || 'Não informado';
          if (!tamanhos[tamanho]) {
            tamanhos[tamanho] = { planejado: 0, produzido: 0 };
          }
          tamanhos[tamanho].planejado += setor.planejada_inicial || 0;
          tamanhos[tamanho].produzido += setor.produzida || 0;
        });
  
        return Object.keys(tamanhos).map(nome => ({
          nome,
          planejado: tamanhos[nome].planejado,
          produzido: tamanhos[nome].produzido,
          progresso: tamanhos[nome].planejado > 0 ? (tamanhos[nome].produzido / tamanhos[nome].planejado) * 100 : 0
        }));
      },
  
      // EXPORTAR PDF
      async exportarPDF() {
        this.exportando = true;
        
        try {
          const conteudo = this.gerarHTMLCompleto();
          const novaJanela = window.open('', '_blank');
          novaJanela.document.write(conteudo);
          novaJanela.document.close();
          
          setTimeout(() => {
            novaJanela.print();
          }, 1000);
          
          this.$emit('relatorio-exportado', {
            formato: 'pdf',
            sucesso: true,
            mensagem: 'PDF aberto em nova janela. Use Ctrl+P para salvar como PDF.'
          });
          
        } catch (error) {
          this.$emit('relatorio-exportado', {
            formato: 'pdf',
            sucesso: false,
            erro: error.message
          });
        } finally {
          this.exportando = false;
        }
      },
  
      // IMPRIMIR
      imprimir() {
        const conteudo = this.gerarHTMLCompleto();
        const novaJanela = window.open('', '_blank');
        novaJanela.document.write(conteudo);
        novaJanela.document.close();
        setTimeout(() => {
          novaJanela.print();
        }, 500);
      },
  
      // GERAR HTML COMPLETO
      gerarHTMLCompleto() {
        const dados = this.dadosRelatorio;
        
        return `
  <!DOCTYPE html>
  <html>
  <head>
      <meta charset="UTF-8">
      <title>Relatório Coração da Produção - Ordem #${dados.ordem}</title>
      <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
          .header { text-align: center; border-bottom: 3px solid #1976D2; padding-bottom: 20px; margin-bottom: 30px; }
          h1 { color: #1976D2; margin: 0; }
          h2 { color: #666; margin: 5px 0; }
          h3 { color: #1976D2; border-bottom: 1px solid #1976D2; padding-bottom: 5px; }
          .metrics { display: flex; justify-content: space-around; margin: 20px 0; }
          .metric { text-align: center; padding: 15px; background: #f5f5f5; border-radius: 8px; min-width: 120px; }
          .metric-value { font-size: 24px; font-weight: bold; color: #1976D2; }
          .metric-label { font-size: 12px; color: #666; margin-top: 5px; }
          table { width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 10px; }
          th, td { border: 1px solid #ddd; padding: 4px; text-align: left; }
          th { background: #1976D2; color: white; }
          @media print { body { margin: 0; } }
      </style>
  </head>
  <body>
      <div class="header">
          <h1>🫀 Relatório do Coração da Produção</h1>
          <h2>Ordem #${dados.ordem}</h2>
          <p>${dados.data}</p>
      </div>
  
      <h3>📊 Resumo Executivo</h3>
      <div class="metrics">
          <div class="metric">
              <div class="metric-value">${dados.metricas.planejado}</div>
              <div class="metric-label">Meta Total</div>
          </div>
          <div class="metric">
              <div class="metric-value">${dados.metricas.produzido}</div>
              <div class="metric-label">Produzido</div>
          </div>
          <div class="metric">
              <div class="metric-value">${dados.metricas.restante}</div>
              <div class="metric-label">Restante</div>
          </div>
          <div class="metric">
              <div class="metric-value">${dados.metricas.erros}</div>
              <div class="metric-label">Erros</div>
          </div>
      </div>
  
      <p><strong>Eficiência Geral: ${dados.metricas.eficiencia}%</strong></p>
  
      ${dados.setores && dados.setores.length > 0 ? `
      <h3>🏭 Status Detalhado por Setor</h3>
      <table>
          <tr>
              <th>Ordem</th>
              <th>Setor</th>
              <th>Parte</th>
              <th>Tamanho</th>
              <th>Planejado</th>
              <th>Recebido</th>
              <th>Produzido</th>
              <th>Disponível</th>
              <th>Responsável</th>
          </tr>
          ${this.getSetoresOrdenados().map(setor => `
          <tr>
              <td>${setor.ordem_exibicao_tela || 'N/A'}º</td>
              <td><strong>${setor.nome_setor || 'N/A'}</strong></td>
              <td>${setor.parte || 'N/A'}</td>
              <td>${setor.tamanho || 'N/A'}</td>
              <td>${setor.planejada_inicial || 0}</td>
              <td>${setor.recebida_aprovada || 0}</td>
              <td>${setor.produzida || 0}</td>
              <td>${setor.saldo_disponivel_produzir || 0}</td>
              <td>${setor.funcionario_atual_nome || 'Não atribuído'}</td>
          </tr>
          `).join('')}
      </table>
      ` : '<p><strong>Nenhum dado de setor disponível</strong></p>'}
  
      <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 2px solid #ddd; font-size: 12px; color: #666;">
          <strong>Relatório gerado pelo Sistema Coração da Produção</strong><br>
          ${new Date().toLocaleString('pt-BR')} | Versão 1.0
      </div>
  </body>
  </html>`;
      }
    }
  };
  </script>
  
  <style scoped>
  .relatorio-content {
    background: white;
  }
  
  @media print {
    .relatorio-content {
      background: white !important;
    }
  }
  </style>